import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { CookieService } from 'ngx-cookie-service';
import { Subject, Subscription } from 'rxjs';
import { FileSizePipe } from 'src/app/_pipes/file-size.pipe';
import { AgentService } from 'src/app/_services/agent.service';
import { ProposalService } from 'src/app/_services/proposal.service';
import { FamilyMember, User } from 'src/app/_types/auth.types';
import { Partner, TokenPartner } from 'src/app/_types/partner.type';
import { FormProduct } from 'src/app/_types/product.types';
import { CustomerAccountType } from 'src/app/enums/customer-account-type.enum';
import { DocumentType } from 'src/app/enums/document-type.enum';
import { EntityType } from 'src/app/enums/entity-type.enum';
import { FamilyMemberType } from 'src/app/enums/family-member-type.enum';
import {
  BUSINESS_INSURANCES,
  getInsuranceTypeLabel,
  InsuranceType,
  PERSONAL_INSURANCES,
} from 'src/app/enums/insurance-type.enum';
import { PartnerType } from 'src/app/enums/partner.enums';
import {
  getPeriodicityByInsuranceType,
  getPeriodicityLabel,
  getPeriodicityMultiplier,
  Periodicity,
  PolicyStatus,
} from 'src/app/enums/proposal.enums';
import { CustomValidators } from 'src/app/helpers/custom.validators';
import { LanguageCode } from 'src/app/helpers/nationalities';
import {
  ProductItemFormGroup,
  ProductSelectionComponent,
} from 'src/app/shared/components/add-proposal-modal/product-selection/product-selection.component';
import { SlideInModalComponent } from 'src/app/shared/components/slide-in-modal/slide-in-modal.component';
import { IconsModule } from 'src/app/shared/icons/icons.module';
import { environment } from 'src/environments/environment';
import { v4 } from 'uuid';

import { CommissionService } from '../../_services/commission.service';
import { CustomerService } from '../../_services/customer.service';
import { DocumentService } from '../../_services/document.service';
import { ProfileService } from '../../_services/profile.service';
import { ToasterNotificationService } from '../../_services/toaster-notification.service';
import { Globals } from '../../globals';

@Component({
  selector: 'app-add-contract-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    SlideInModalComponent,
    ProductSelectionComponent,
    FileSizePipe,
    IconsModule,
  ],
  templateUrl: './add-contract-modal.component.html',
  styleUrls: ['./add-contract-modal.component.css'],
})
export class AddContractModalComponent implements OnInit, OnDestroy {
  DATE_FORMAT = environment.DATE_FORMAT;

  user: TokenPartner;

  leadId: string = '';

  languageCode =
    (this.cookieService.get('selectedCountryCode') as LanguageCode) || 'fr';

  periodicitySubscription!: Subscription;

  coversPeriodicitySubscription!: Subscription;

  productListSubscription!: Subscription;

  insuranceTypeSubscription!: Subscription;

  @Input()
  custId!: string;

  @Input()
  centralisationId: string;

  @Input()
  insuranceType?: InsuranceType | null;

  @Input()
  insurer?: string | null;

  formId = v4();

  isCompanyCustomer: boolean;

  constructor(
    private readonly fb: FormBuilder,
    private readonly CustomerService: CustomerService,
    private readonly CommissionService: CommissionService,
    private readonly documentService: DocumentService,
    private readonly ProposalService: ProposalService,
    private readonly globals: Globals,
    private readonly ToasterNotificationService: ToasterNotificationService,
    private readonly profileservice: ProfileService,
    private readonly datePipe: DatePipe,
    private readonly cookieService: CookieService,
    private readonly agentService: AgentService
  ) {
    this.user = this.globals.getUser();
  }

  familyMembers: FamilyMember[] = [];

  ngOnInit(): void {
    this.getProfile();
    this.GetCustomerDetail();
    if (this.user.userType === PartnerType.PARTNER) {
      this._getAgentList();
    }
    if (this.custId && this.custId !== '') this.fetchFamilyMembers(this.custId);

    this.productListSubscription = this.form.controls[
      'productList'
    ].valueChanges.subscribe((pdata) => {
      this.setTotalAmount(pdata, this.form.value.coversPeriodicity);
    });
    this.form.controls['periodicity'].valueChanges.subscribe(
      (periodicityVal) => {
        this.setPremiumAmount(
          parseFloat(this.form.value.totalAmount) ?? 0,
          periodicityVal as Periodicity
        );
      }
    );

    this.coversPeriodicitySubscription = this.form.controls[
      'coversPeriodicity'
    ].valueChanges.subscribe((coverPeriodicity) => {
      this.setTotalAmount(
        this.form.controls.productList.value,
        coverPeriodicity
      );
    });

    this.insuranceTypeSubscription = this.form.controls[
      'insuranceType'
    ].valueChanges.subscribe((type) => {
      this.form.controls.coversPeriodicity.setValue(
        getPeriodicityByInsuranceType(type)
      );
    });
  }

  // The covers periodicity field will be used to compute the total amount due
  // The (payment) periodicity will be used to compute the premium amount
  setTotalAmount(
    productsList: Partial<FormProduct>[],
    coversPeriodicity: Periodicity
  ) {
    let totalAmount = 0;
    for (let i = 0; i < productsList.length; i++) {
      totalAmount += !Number.isNaN(productsList[i].amount)
        ? (productsList[i].amount as number)
        : 0;
    }
    totalAmount *= getPeriodicityMultiplier(coversPeriodicity);
    this.form.controls.totalAmount.setValue(totalAmount.toFixed(2));
    if (totalAmount > 0) {
      const { periodicity } = this.form.value;
      this.setPremiumAmount(totalAmount, periodicity as Periodicity);
    }
  }

  setPremiumAmount(totalAmount: number, periodicity: Periodicity = null) {
    this.form.controls.premiumAmount.setValue(
      (totalAmount / getPeriodicityMultiplier(periodicity)).toFixed(2)
    );
  }

  getpremiumAmount(totalAmount: any = 0, periodicity: any = '') {
    let premiumAmount: any = 0;
    if (periodicity === 'Month') {
      premiumAmount = totalAmount / 12;
      premiumAmount = parseFloat(premiumAmount);
    } else if (periodicity === 'Quarter') {
      premiumAmount = totalAmount / 4;
      premiumAmount = parseFloat(premiumAmount);
    } else if (periodicity === 'Semester') {
      premiumAmount = totalAmount / 2;
      premiumAmount = parseFloat(premiumAmount);
    } else {
      premiumAmount = totalAmount;
    }
    const ppObj = {
      premiumAmount:
        premiumAmount && premiumAmount > 0
          ? Number(premiumAmount).toFixed(2)
          : '',
    };
    this.form.patchValue(ppObj);
  }

  isLinear = true;

  isProductMultiple = false;

  form = this.fb.group(
    {
      policyId: new FormControl('', [Validators.required]),
      insuranceType: new FormControl<InsuranceType | null>(null, [
        Validators.required,
      ]),
      startDate: new FormControl(new Date(), [Validators.required]),
      endDate: new FormControl(new Date(), [Validators.required]),
      categoryId: new FormControl('', [Validators.required]),
      subCategoryId: new FormControl('', [Validators.required]),
      product: new FormControl([], [Validators.required]),
      agentId: new FormControl('', []),
      periodicity: new FormControl<Periodicity>(Periodicity.YEAR, [
        Validators.required,
      ]),
      coversPeriodicity: new FormControl<Periodicity>(Periodicity.YEAR),
      premiumAmount: new FormControl('', [Validators.required]),
      totalAmount: new FormControl('', [Validators.required]),
      productList: this.fb.array<FormGroup<ProductItemFormGroup>>(
        [],
        [Validators.required]
      ),
      familyMemberId: ['', Validators.required],
      categoryName: [''],
      categoryNameInFrench: [''],
      categoryLogo: [''],
      subCategoryName: [''],
      subCategoryNameInFrench: [''],
    },
    {
      validators: CustomValidators.dateAfter('endDate', 'startDate'),
    }
  );

  get contractError() {
    return this.form.controls;
  }

  newCatProduct(): FormGroup {
    return this.fb.group({
      productId: ['', []],
      productName: ['', [Validators.required]],
      amount: ['', [Validators.required]],
    });
  }

  addNewCatProduct() {
    this.productList.push(this.newCatProduct());
  }

  removeCatProduct(i: number) {
    this.productList.removeAt(i);
  }

  get productList(): FormArray {
    return <FormArray>this.form.get('productList');
  }

  GetCustomerdata: User;

  GetCustomerDetail(): any {
    this.CustomerService._getCustomerDetails({
      entityId: this.custId,
      entityType: EntityType.USER_ID,
    }).subscribe({
      next: (x: any) => {
        this.GetCustomerdata = x.data;
        this.isCompanyCustomer = x.data.type === CustomerAccountType.COMPANY;
        this.insuranceTypeListData = (
          this.isCompanyCustomer ? BUSINESS_INSURANCES : PERSONAL_INSURANCES
        ).filter((x) => x !== InsuranceType.CENTRALISATION);
        if (this.form?.controls?.agentId)
          this.form.controls.agentId.setValue(this.GetCustomerdata.agentId);
      },
    });
  }

  memberList: FamilyMember[] = [];

  isSubmited = false;

  isClicked = false;

  _addContract() {
    if (
      !this.policyFiles ||
      this.policyFiles.length === 0 ||
      this.form.invalid
    ) {
      return;
    }
    if (this.isClicked) {
      return;
    }
    this.isSubmited = true;
    this.isClicked = true;

    const catProductArray: any[] = [];

    this.documentService
      .uploadDocuments(
        this.policyFiles,
        this.custId,
        this.form.value.familyMemberId!,
        DocumentType.OTHER
      )
      .subscribe({
        next: (uploadedDocuments) => {
          const productJson: Array<any> = this.form.value.productList
            ? this.form.value.productList
            : [];
          for (let i = 0; i < productJson.length; i++) {
            catProductArray.push({
              productId: productJson[i].productId,
              productName: productJson[i].productName,
              amount: productJson[i].amount,
            });
          }
          const startDate: any = this.datePipe.transform(
            this.form.value.startDate,
            'yyyy-MM-dd'
          );
          const endDate: any = this.datePipe.transform(
            this.form.value.endDate,
            'yyyy-MM-dd'
          );

          const formData = {
            partnerId:
              this.user.userType === 'AGENT'
                ? this.profileData.source
                : this.user.partnerId,
            policyId: this.form.value.policyId,
            insuranceType: this.form.value.insuranceType,
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
            periodicity: this.form.value.periodicity,
            coversPeriodicity: this.form.value.coversPeriodicity,
            premiumAmount: this.form.value.premiumAmount,
            offerType: 'EXISTING',
            categoryDetails: {
              categoryId: this.form.value.categoryId,
              categoryName: this.form.value.categoryName,
              categoryNameInFrench: this.form.value.categoryNameInFrench,
              categoryLogo: this.form.value.categoryLogo,
              subCategoryId: this.form.value.subCategoryId,
              subCategoryName: this.form.value.subCategoryName,
              subCategoryNameInFrench: this.form.value.subCategoryNameInFrench,
            },
            productDetails: {
              productList: catProductArray,
            },
            agentId:
              this.user.userType === 'AGENT'
                ? this.user.partnerId
                : this.form.value.agentId,
            memberId: this.custId,
            totalAmount: this.form.value.totalAmount,
            contractDoc: uploadedDocuments.map(
              (document) => document.documentId
            ),
            familyMemberId: this.form.value.familyMemberId,
            policyStatus: PolicyStatus.ACTIVE,
            centralisationId: this.centralisationId,
          };

          this.ProposalService._addExistingContract(formData).subscribe({
            next: (result: any) => {
              this.isSubmited = false;
              this.isClicked = false;
              if (result.status == 'SUCCESS') {
                this.ToasterNotificationService.showSuccess(
                  $localize`Contrat ajouté avec succès`
                );
                this.policyAdded.emit();
                this.closeModal();
                this.resetForm();
                this.isClicked = false;
                this.isSubmited = false;
              } else {
                this.ToasterNotificationService.showError(
                  result.status,
                  'Error',
                  '2000'
                );
              }
            },
            error: (err: any) => {
              this.isSubmited = false;
              this.isClicked = false;
              let errorm = err.error.error.clientErrorMessage;
              if (this.languageCode === 'fr') {
                errorm = err.error.error.clientErrorMessageInFrench;
              }
              this.ToasterNotificationService.showError(errorm, '', 3000);
            },
          });
        },
        error: () => {
          this.ToasterNotificationService.showError(
            $localize`Une erreur est survenue lors de l'ajout des documents`
          );
          this.isClicked = false;
          this.isSubmited = false;
        },
      });
  }

  /* Get ALL active agents */
  agentArryData: Partner[] = [];

  isAgentDisabled = false;

  _getCategorytAgentList() {
    const obj = {
      partnerId: this.user.userType === 'AGENT' ? this.profileData.source : '',
      categoryId: this.form.value.categoryId,
      subCategoryId: this.form.value.subCategoryId,
    };
    this.CommissionService._getCategorytAgentList(obj).subscribe({
      next: (data: any) => {
        this.agentArryData = data.data.agentList;
        if (
          (this.leadId != '' && this.leadId != null) ||
          this.user.userType == 'AGENT'
        ) {
          const step2obj = {
            agentId: this.leadDetailsData
              ? this.leadDetailsData.agentId
              : this.user.partnerId,
          };
          this.form.patchValue(step2obj);
          this.isAgentDisabled = true;
        }
      },
    });
  }

  isMemberDisabled = false;

  isMemberIds = false;

  customerIdsArray: any = [];

  insuranceTypeListData: InsuranceType[] = [];

  /* Get lead details */
  isInduranceTypeDisabled = false;

  isAgentTypeDisabled = false;

  leadDetailsData: any = '';

  profileData: any;

  getProfile() {
    this.profileservice._getProfile().subscribe({
      next: async (result: any) => {
        if (result.status == 'SUCCESS') {
          this.profileData = result.data;
        }
      },
    });
  }

  getInsuranceTypeLabel = getInsuranceTypeLabel;

  fetchFamilyMembers(userId: string) {
    this.CustomerService._getAllCustomerFamilyList({ userId }).subscribe({
      next: (res) => {
        this.familyMembers = res.data.familyMemberList;
        if (this.isCompanyCustomer) {
          this.form.controls.familyMemberId.setValue(
            this.familyMembers.find(
              (x) => x.memberType === FamilyMemberType.SELF
            ).familyMemberId
          );
        }
      },
    });
  }

  categoryListFetchSubject = new Subject<boolean>();

  Periodicity = Object.values(Periodicity);

  getPeriodicityLabel = getPeriodicityLabel;

  @ViewChild('modal') modal!: SlideInModalComponent;

  submitted = false;

  @Output()
  policyAdded = new EventEmitter();

  openModal() {
    this.resetForm();
    this.modal.openModal();
  }

  closeModal() {
    this.modal.closeModal(false);
  }

  policyFiles: File[] = [];

  onDocumentUpload(event: Event) {
    const uploadedFiles: File[] = Array.from(
      (event.target as HTMLInputElement).files!
    );
    uploadedFiles.forEach((file) => {
      if (
        !this.policyFiles.some(
          (f) =>
            f.type === file.type && f.size === file.size && f.name === file.name
        )
      ) {
        this.policyFiles.push(file);
      }
    });
  }

  removeFile(index: number) {
    this.policyFiles.splice(index, 1);
  }

  @ViewChild('productSelection')
  productSelectionComponent: ProductSelectionComponent;

  resetForm() {
    this.form.reset();
    this.policyFiles = [];
    this.familyMembers = [];
    if (this.custId && this.custId !== '') this.fetchFamilyMembers(this.custId);
    this.productSelectionComponent.resetProductSelection();
    if (this.insuranceType) {
      this.form.controls.insuranceType.setValue(this.insuranceType);
      this.productSelectionComponent.findChosenInsurer(false);
    }
  }

  agentsList: Partner[] = [];

  _getAgentList() {
    const obj = {
      partnerId: this.user.partnerId,
      searchBy: '',
    };
    this.agentService._getAgentList(obj).subscribe({
      next: (result: any) => {
        if (result.status == 'SUCCESS') {
          this.agentsList = result.data.agentsList;
        }
      },
    });
  }

  ngOnDestroy(): void {
    if (this.periodicitySubscription)
      this.periodicitySubscription.unsubscribe();
    if (this.coversPeriodicitySubscription)
      this.coversPeriodicitySubscription.unsubscribe();
    if (this.productListSubscription)
      this.productListSubscription.unsubscribe();
    if (this.insuranceTypeSubscription)
      this.insuranceTypeSubscription.unsubscribe();
  }
}
